import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, MessageSquare, ThumbsUp, Eye, CheckCircle, Star, Heart, Image as ImageIcon, Trash2, Edit, Copy, Save, X } from 'lucide-react';
interface ArchivedForumQuestionDetailProps {
  question: {
    id: number;
    title?: string;
    content: string;
    subject: string;
    topic: string;
    image?: string;
    forumDate?: string;
    archivedDate: string;
    notes?: string;
    noteImages?: string[];
    bestAnswer?: string | null;
    teacherApproved?: string | null;
    mostLiked?: string | null;
  };
  onClose: () => void;
  onUpdateNotes: (id: number, notes: string) => void;
  onDeleteNoteImage: (id: number, imageUrl: string) => void;
}
export function ArchivedForumQuestionDetail({
  question,
  onClose,
  onUpdateNotes,
  onDeleteNoteImage
}: ArchivedForumQuestionDetailProps) {
  const navigate = useNavigate();
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(question.notes || '');
  const handleViewFullDiscussion = () => {
    // Navigate to forum with both URL param and state
    navigate(`/forum/konu/${question.id}`, {
      state: {
        openTopicId: question.id,
        fromArchive: true
      }
    });
  };
  const handleSaveNotes = () => {
    onUpdateNotes(question.id, notesDraft);
    setIsEditingNotes(false);
  };
  return <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-64">
      <div className="relative p-6 bg-black/90 rounded-xl" data-id="element-65">
        <div className="flex flex-col space-y-6" data-id="element-66">
          {/* Header with back button */}
          <div className="flex items-center justify-between" data-id="element-67">
            <button onClick={onClose} className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors" data-id="element-68">
              <ChevronLeft className="w-5 h-5" data-id="element-69" />
              <span data-id="element-70">Arşive Geri Dön</span>
            </button>
            <div className="flex items-center gap-3" data-id="element-71">
              <button onClick={handleViewFullDiscussion} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg text-white hover:opacity-90 transition-colors" data-id="element-72">
                <MessageSquare className="w-4 h-4" data-id="element-73" />
                <span data-id="element-74">Tartışmanın Tamamını Gör</span>
              </button>
            </div>
          </div>
          {/* Question content */}
          <div className="space-y-4" data-id="element-75">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-gray-400" data-id="element-76">
              <span className="px-2 py-1 rounded-full bg-purple-400/10 text-purple-400" data-id="element-77">
                {question.subject}
              </span>
              {question.topic && <span className="px-2 py-1 rounded-full bg-blue-400/10 text-blue-400" data-id="element-78">
                  {question.topic}
                </span>}
              <span data-id="element-79">
                Forum: {question.forumDate || 'Bilinmiyor'} • Arşiv:{' '}
                {new Date(question.archivedDate).toLocaleDateString('tr-TR')}
              </span>
            </div>
            <div className="prose prose-invert max-w-none" data-id="element-80">
              <p data-id="element-81">{question.content}</p>
            </div>
            {question.image && <div className="mt-4" data-id="element-82">
                <img src={question.image} alt="Soru görseli" className="rounded-lg max-w-full max-h-96 mx-auto" data-id="element-83" />
              </div>}
          </div>
          {/* Best answers section */}
          {(question.teacherApproved || question.bestAnswer || question.mostLiked) && <div className="space-y-4 pt-4 border-t border-gray-800" data-id="element-84">
              <h3 className="text-lg font-medium" data-id="element-85">En İyi Cevaplar</h3>
              {question.teacherApproved && <div className="p-4 bg-black/50 border border-green-500/30 rounded-lg" data-id="element-86">
                  <div className="flex items-center gap-2 mb-2 text-green-400" data-id="element-87">
                    <CheckCircle className="w-5 h-5" data-id="element-88" />
                    <span className="font-medium" data-id="element-89">Öğretmen Onaylı Çözüm</span>
                  </div>
                  <p className="text-gray-300" data-id="element-90">{question.teacherApproved}</p>
                </div>}
              {question.bestAnswer && <div className="p-4 bg-black/50 border border-blue-500/30 rounded-lg" data-id="element-91">
                  <div className="flex items-center gap-2 mb-2 text-blue-400" data-id="element-92">
                    <Star className="w-5 h-5" data-id="element-93" />
                    <span className="font-medium" data-id="element-94">Aradığım Cevap</span>
                  </div>
                  <p className="text-gray-300" data-id="element-95">{question.bestAnswer}</p>
                </div>}
              {question.mostLiked && <div className="p-4 bg-black/50 border border-purple-500/30 rounded-lg" data-id="element-96">
                  <div className="flex items-center gap-2 mb-2 text-purple-400" data-id="element-97">
                    <Heart className="w-5 h-5" data-id="element-98" />
                    <span className="font-medium" data-id="element-99">En Beğenilen Yanıt</span>
                  </div>
                  <p className="text-gray-300" data-id="element-100">{question.mostLiked}</p>
                </div>}
            </div>}
          {/* Notes section */}
          <div className="space-y-4 pt-4 border-t border-gray-800" data-id="element-101">
            <div className="flex items-center justify-between" data-id="element-102">
              <h3 className="text-lg font-medium" data-id="element-103">Notlarım</h3>
              <div className="flex gap-2" data-id="element-104">
                {isEditingNotes ? <>
                    <button onClick={handleSaveNotes} className="flex items-center gap-1 text-green-400 hover:text-green-300 transition-colors" data-id="element-105">
                      <Save className="w-4 h-4" data-id="element-106" />
                      <span data-id="element-107">Kaydet</span>
                    </button>
                    <button onClick={() => {
                  setIsEditingNotes(false);
                  setNotesDraft(question.notes || '');
                }} className="flex items-center gap-1 text-red-400 hover:text-red-300 transition-colors" data-id="element-108">
                      <X className="w-4 h-4" data-id="element-109" />
                      <span data-id="element-110">İptal</span>
                    </button>
                  </> : <button onClick={() => setIsEditingNotes(true)} className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors" data-id="element-111">
                    <Edit className="w-4 h-4" data-id="element-112" />
                    <span data-id="element-113">Düzenle</span>
                  </button>}
              </div>
            </div>
            {isEditingNotes ? <textarea value={notesDraft} onChange={e => setNotesDraft(e.target.value)} placeholder="Bu soru hakkında notlarınızı buraya yazın..." className="w-full h-32 p-3 bg-black/50 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500/60" data-id="element-114" /> : <div className="p-4 bg-black/50 border border-purple-500/30 rounded-lg min-h-[80px]" data-id="element-115">
                {question.notes ? <p className="text-gray-300" data-id="element-116">{question.notes}</p> : <p className="text-gray-500 italic" data-id="element-117">
                    Henüz not eklenmemiş. Düzenle butonuna tıklayarak not
                    ekleyebilirsiniz.
                  </p>}
              </div>}
            {/* Note images */}
            {question.noteImages && question.noteImages.length > 0 && <div className="space-y-3" data-id="element-118">
                <h4 className="text-md font-medium" data-id="element-119">Görseller</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-id="element-120">
                  {question.noteImages.map((image, index) => <div key={index} className="relative group aspect-video bg-black/50 border border-purple-500/30 rounded-lg overflow-hidden" data-id="element-121">
                      <img src={image} alt={`Not görseli ${index + 1}`} className="w-full h-full object-cover" data-id="element-122" />
                      <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity" data-id="element-123">
                        <button onClick={() => onDeleteNoteImage(question.id, image)} className="p-2 bg-red-500/20 text-red-400 rounded-full hover:bg-red-500/30 transition-colors" data-id="element-124">
                          <Trash2 className="w-5 h-5" data-id="element-125" />
                        </button>
                      </div>
                    </div>)}
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}